import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'
import type { Theme } from '@mui/material/styles'
import React, { Fragment, useEffect, useState } from 'react'
import { Modal } from '../../_EOS/Modal'
import { DocumentPrint } from '../../UI/DocumentPrint/DocumentPrint'
import { useApi } from '../../../hooks/useApi'
import { ApiResponse } from '../../../typings/ApiResponse'
import { Status } from '../../../hooks/useFetchState'
import { WorkOrder } from '../../../interfaces'
import { PickingRouteStatusItem } from '../../../interfaces/WorkOrder/WorkOrder'
import { OrderLabelInfoDto } from '../../../interfaces/OrderLabel'
import {
  createOrderLabel,
  getOrderLabelList,
  getPickingRoutes,
  getWorkOrder,
  requestOrderLabel
} from './api'
import { PickingUsingExternalCourierLabel } from './PickingUsingExternalCourierLabel'
import { LabelError } from './typings'
import { useConfiguration } from '../../../hooks/useConfiguration'
import { PickingValidateHeader } from '../../../interfaces/Picking/Picking'
import { eValidationStatus } from '../../../enums/eValidationStatus'

interface PickingLabelActionsProps {
  handlePrintDocument: (labelType: string) => void
  showModalPrint: (isOpen: boolean) => void
  isPrintLabelVisible: boolean
  workOrderId: number
  isUsingExternalCourierLabel: boolean
  isUsingInternalCourierLabel: boolean
  pickingHeader: PickingValidateHeader | undefined
  handleSnackbar: (message: string, success: boolean) => void
  handleShowLabelButton: (value: boolean) => void
  executeGetLabelCourier: boolean
  sethasMultipleUnvalidatedRoutes: React.Dispatch<React.SetStateAction<boolean>>
}

export const PickingLabelActions: React.FC<PickingLabelActionsProps> = ({
  handlePrintDocument,
  showModalPrint,
  isPrintLabelVisible,
  workOrderId,
  isUsingExternalCourierLabel,
  isUsingInternalCourierLabel,
  pickingHeader,
  handleSnackbar,
  handleShowLabelButton,
  executeGetLabelCourier,
  sethasMultipleUnvalidatedRoutes
}) => {
  const theme = useTheme()
  const matched = useMediaQuery(theme.breakpoints.down('md'))
  const classes = useStyles()

  const [showPDF, setShowPDF] = useState(false)
  const [showTemporalLabel, setShowTemporalLabel] = useState(false)
  const [temporalLabel, setTemporalLabel] = useState<string>()
  const [error, setError] = useState<LabelError | undefined>(undefined)

  const { state, callApi } = useApi<ApiResponse<OrderLabelInfoDto>>()
  const { state: ordersState, callApi: ordersCallApi } = useApi<WorkOrder>()
  const { callApi: createCallApi } = useApi()
  const { state: requestLabelState, callApi: requestLabelCallApi } = useApi()
  const { state: pickingRoutesState, callApi: pickingRoutesCallApi } =
    useApi<PickingRouteStatusItem[]>()
  const [showBulkButton, setShowBulkButton] = useState(false)
  const { configurationState, getConfiguration } = useConfiguration()

  const calculateMissingQty = () => {
    const realQty =
      pickingHeader?.realQty === undefined ? 0 : pickingHeader?.realQty

    const pickedQty =
      pickingHeader?.pickedQty === undefined ? 0 : pickingHeader.pickedQty
    return realQty - pickedQty
  }

  const fetchPickingRouteData = (workOrderId: number) => {
    pickingRoutesCallApi(getPickingRoutes(workOrderId))
  }
  const fetchOrderLabelData = (orderId: number) => {
    callApi(getOrderLabelList(orderId))
  }

  const fetchWorkOrder = () => {
    ordersCallApi(getWorkOrder(workOrderId))
  }

  const sendRequestCreateOrderLabel = (orderId: number) => {
    createCallApi(
      createOrderLabel({
        orderId,
        statusId: 1,
        orderLabelUrl: '',
        packageQuantity: 1
      })
    ).then(() => fetchOrderLabelData(orderId))
  }

  // Preguntamos por pedidos
  useEffect(() => {
    fetchWorkOrder()
  }, [])

  // Si OT solo tiene 1 pedido
  // Consultamos la data de picking
  // De lo contrario, mostramos error
  useEffect(() => {
    if (ordersState.status > 1 && ordersState.data !== undefined) {
      if (
        ordersState.data?.orderDetails !== undefined &&
        ordersState.data?.orderDetails?.length === 1
      ) {
        fetchPickingRouteData(workOrderId)
        setError(undefined)
      } else {
        setError({
          errorDescription:
            'NO ES POSIBLE GENERAR ETIQUETA YA QUE LA OT CONTIENE MAS DE UN PEDIDO',
          typeError: 'GREATER_THAN_ONE_ORDER'
        })
      }
    }
  }, [ordersState])

  // Si no tenemos ningún error y obtuvimos correctamente la info de pedidos
  // Y además todas las rutas están validadas o solo posee 1 ruta
  useEffect(() => {
    const isSuccess = ordersState.status === Status.Success
    const hasNoError = error === undefined
    const pickingRoutes = pickingRoutesState.data

    if (isSuccess && hasNoError) {
      if (pickingRoutes) {
        const orderId = ordersState.data?.orderDetails?.[0]?.id
        if (orderId) {
          fetchOrderLabelData(orderId)
        }
      }
    }
  }, [pickingRoutesState, ordersState])

  // Si se cumplen las condiciones, consultamos estado de etiqueta
  // Es decir: existe registro de etiqueta? cual es su estado?
  useEffect(() => {
    if (state.status === Status.Success && state.data?.items.length === 0) {
      if (
        ordersState.data?.orderDetails !== undefined &&
        ordersState.data?.orderDetails?.length === 1
      ) {
        sendRequestCreateOrderLabel(ordersState.data?.orderDetails[0].id)
      }
    }
    if (configurationState === undefined) {
      getConfiguration()
    } else {
      const packagesLabelConfiguration =
        configurationState.configuration.filter(
          (el) => el.name === 'PackagesLabel'
        )[0]

      if (packagesLabelConfiguration !== undefined) {
        setShowBulkButton(packagesLabelConfiguration.value === 'true')
      }
    }
  }, [state])

  // Se copia validación existente, dejando en useeffect. Cuando los llamados a OrderLabel:State, PickingRoute, y WorkOrders tengan su info.
  // Asigna el valor del State "sethasMultipleUnvalidatedRoutes" el cual se usa para mostrar y/o ocultar el boton Imprimir etiqueta del Courier.
  useEffect(() => {
    // indica si las rutas estan finalizadas
    const pickingRoutes = pickingRoutesState.data
    const allRoutesValidated = pickingRoutes
      ? pickingRoutes.length === 1 ||
        pickingRoutes.every(
          (el) => el.validationStatusId === eValidationStatus.Finished
        )
      : false

    const hasMultipleUnvalidatedRoutes =
      pickingRoutes && pickingRoutes.length > 1 && !allRoutesValidated

    // indica si ya tiene la url de la etiqueta. De no existir: se oculta el boton imprimir (courier).
    const shouldTemporalLabelAppear =
      !state.data?.items ||
      state.data?.items.length <= 0 ||
      !state.data?.items[0].orderLabelUrl ||
      hasMultipleUnvalidatedRoutes // si tienes multiples rutas sin validar queda en true

    // asigna valor para mostrar etiqueta courier
    sethasMultipleUnvalidatedRoutes(Boolean(shouldTemporalLabelAppear))
  }, [state, pickingRoutesState, ordersState])

  // OrderLabel packageQuantityState
  const [isOpenPackageQuantityModal, setOpenPackageQuantityModal] =
    useState(false)
  const [packageQuantityInputValue, setPackageQuantityInputValue] =
    useState('2')

  const sendRequestLabel = () => {
    requestLabelCallApi(
      requestOrderLabel(workOrderId, packageQuantityInputValue)
    )
      .then(() => {
        setOpenPackageQuantityModal(false)
        fetchOrderLabelData(workOrderId)
      })
      .catch((error) =>
        setError({
          errorDescription: error,
          typeError: 'OTHER_ERROR'
        })
      )
  }

  const existOrderLabelUrl = () => {
    if (state.data?.items[0] === undefined || state.data?.items[0] === null)
      return false

    if (
      state.data?.items[0] !== undefined &&
      state.data?.items[0].orderLabelUrl === ''
    )
      return false

    return true
  }

  return !isUsingExternalCourierLabel && !isUsingInternalCourierLabel ? null : (
    <Box py='20px'>
      <Box px='5px'>
        <Grid container alignContent='flex-end' alignItems='flex-end'>
          <Grid item xs={12} md={6}>
            {!matched && isUsingInternalCourierLabel && (
              <Typography variant='h6'>VISUALIZAR</Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {!matched && isUsingExternalCourierLabel && (
              <Typography variant='h6'>ETIQUETA TRANSPORTISTA</Typography>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box component={Paper} padding='30px'>
        <Grid container spacing={5}>
          <Grid item xs={3} md={3} justifyContent='flex-start' spacing={2}>
            {isUsingInternalCourierLabel && (
              <Fragment>
                {matched && <Typography>Visualizar</Typography>}
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => {
                    handlePrintDocument('GetTicketReport')
                    showModalPrint(true)
                  }}
                  style={{
                    visibility: isPrintLabelVisible ? 'visible' : 'hidden'
                  }}
                  fullWidth={matched}
                  className={classes.button}
                >
                  Imprimir Etiqueta
                </Button>
                <Button
                  className={classes.button}
                  variant='outlined'
                  color='primary'
                  onClick={() => {
                    handlePrintDocument('GetValidationReport')
                    showModalPrint(true)
                  }}
                  fullWidth={matched}
                >
                  Imprimir Hoja de Validación
                </Button>
              </Fragment>
            )}
          </Grid>
          <Grid
            container
            item
            xs={6}
            md={6}
            spacing={2}
            justifyContent='flex-start'
            direction='row'
          >
            <Grid item xs={4}>
              <Typography align='center'>
                <strong>SKU: </strong>
                {pickingHeader?.totalSku}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography align='center'>
                <strong>QTY: </strong>
                {pickingHeader?.realQty === undefined
                  ? 0
                  : pickingHeader?.realQty}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography align='center'>
                <strong>FALTANTE: </strong>
                {calculateMissingQty()}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            item
            xs={3}
            md={3}
            style={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <PickingUsingExternalCourierLabel
              orderLabelState={state}
              pickingRoutesState={pickingRoutesState}
              matched={matched}
              error={error}
              setOpenPackageQuantityModal={setOpenPackageQuantityModal}
              setShowPDF={setShowPDF}
              showBulkButton={showBulkButton}
              setShowTemporalLabel={setShowTemporalLabel}
              setTemporalLabel={setTemporalLabel}
              pickingRouteId={pickingHeader?.pickingRouteId as number}
              handleSnackbar={handleSnackbar}
              showCourierLabel={isUsingExternalCourierLabel}
              fetchOrderLabelData={fetchOrderLabelData}
              handleShowLabelButton={handleShowLabelButton}
              executeGetLabelCourier={executeGetLabelCourier}
            />
          </Grid>
        </Grid>
      </Box>

      <Modal
        onClose={() => setOpenPackageQuantityModal(false)}
        open={isOpenPackageQuantityModal}
        title='Solicitar Etiqueta'
        maxWidth='xs'
        cancelButton={{
          title: 'Cancelar',
          action: () => setOpenPackageQuantityModal(false)
        }}
        successButton={{
          title: 'Generar Etiqueta',
          action: sendRequestLabel
        }}
        loading={requestLabelState.status === Status.Loading}
      >
        <Box className={classes.modalBox}>
          <Typography variant='body1'>Ingrese cantidad de bultos</Typography>
          <TextField
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPackageQuantityInputValue(e.target.value)
            }
            value={packageQuantityInputValue}
            type='number'
            InputProps={{
              inputProps: { min: 1, max: 10, style: { textAlign: 'center' } }
            }}
            disabled={requestLabelState.status === Status.Loading}
            fullWidth
            variant='outlined'
          />
        </Box>
      </Modal>

      <Modal
        title='Etiqueta transportista'
        onClose={() => setShowPDF(false)}
        open={showPDF}
      >
        {existOrderLabelUrl() ? (
          <DocumentPrint
            documentPDF={
              state.data?.items[0] !== undefined
                ? state.data?.items[0].orderLabelUrl
                : ''
            }
          />
        ) : (
          <Typography variant='body2'>
            No se encuentra todavía la etiqueta
          </Typography>
        )}
      </Modal>

      <Modal
        title='Etiqueta temporal'
        maxWidth='md'
        onClose={() => setShowTemporalLabel(false)}
        open={showTemporalLabel}
      >
        <DocumentPrint documentPDF={temporalLabel as string} type='base64' />
      </Modal>
    </Box>
  )
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      [theme.breakpoints.up('md')]: {
        marginLeft: '5px'
      }
    },
    modalBox: {
      padding: '50px',
      alignItems: 'center',
      justifyContent: 'space-between',
      display: 'flex',
      flexDirection: 'column'
    },
    errorMessage: {
      color: 'red',
      verticalAlign: 'center'
    }
  })
)
